import React, { Component } from 'react';

import {
    CircularProgress,
    Grid,
    Paper,
    Avatar,
    Typography
} from '@material-ui/core/';

import Moment from 'moment';
import { AxiosRequest } from '@apricityhealth/web-common-lib/utils/Axios';
import Config from '@apricityhealth/web-common-lib/Config';
import _ from 'lodash';
import T from 'i18n-react';

import { Logger } from '@apricityhealth/web-common-lib/utils/Logger';
import { findPeakAlert } from 'utils';
import { getErrorMessage } from '@apricityhealth/web-common-lib/utils/getErrorMessage';

import PhoneCallbackIcon from '@material-ui/icons/PhoneCallback';
import PhoneMissedIcon from '@material-ui/icons/PhoneMissed';

const log = new Logger();

export class HistoryView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            days: [],
            data: [],
            journals: [],
            progress: null,
            dialog: null
        };
    }

    componentDidMount() {
        this.loadContent();
    }

    loadContent(retries = 0) {
        const loadContentTime = Date.now();
        const { appContext: { state: { patientId, alertLevels, alertLevelsLoaded, configLoaded, cache, idToken, config }}} = this.props;

        if (!alertLevelsLoaded || !configLoaded) {
            log.debug(`Waiting for alerts: ${alertLevelsLoaded}, config: ${configLoaded}`);
            // not all data loaded by the main app yet, wait until we have a config and alert levels
            this.setState({ progress: <CircularProgress size={30} /> });
            return setTimeout(() => this.loadContent(), 250);
        }

        const overallAlertId = _.get(config, 'dashboard.homeStatus.overallAlertId') || _.get(config, 'dashboard.employeeStatus.overallAlertId') || Config.overallAlertId;
        const timezone = Moment.tz.guess();
        const endTime = Moment.tz(Moment(), timezone).endOf('day').toDate();
        const startTime = Moment.tz(endTime, timezone).subtract(7, 'days').toDate(); //.startOf('day').toDate();
        const dataIds = [overallAlertId, 'disposition' ];
        
        const getData = {
            url: Config.baseUrl + `${Config.pathPrefix}patients/${patientId}/data?dataId=${dataIds.join(',')}&days=14`,
            method: 'GET',
            headers: { "Authorization": idToken },
        };
        const getDataCacheKey = "HomeStatusView.getData." + patientId + "." + dataIds.join(',');
        log.debug("getData Request:", getData);

        const getJournals = {
            url: Config.baseUrl + `${Config.pathPrefix}journal/${patientId}?&days=14`,
            method: 'GET',
            headers: { "Authorization": idToken },
        };
        const getJournalsKey = "HomeStatusView.getJournals." + patientId;
        log.debug("getJournals request:", getJournals);

        this.setState({ progress: <CircularProgress style={{ marginTop: '10px' }} size={30} />, error: null });
        Promise.all([
            cache[getDataCacheKey] ? Promise.resolve(cache[getDataCacheKey]) : AxiosRequest(getData).then((response) => {
                cache[getDataCacheKey] = response;
                return response;
            }),
            cache[getJournalsKey] ? Promise.resolve(cache[getJournalsKey]) : AxiosRequest(getJournals).then((response) => {
                cache[getJournalsKey] = response;
                return response;
            })
        ]).then(([dataResponse, journalsResponse]) => {

            const data = _.get(dataResponse, "data.data", [] );
            log.debug("getData Result:", data);
            const journals = _.get(journalsResponse, "data.records", []);
            log.debug("getJournals Response:", journals);

            const dataWithJournals = data.filter(i => journals.some(journal => journal.sessionId === i.sessionId));

            const days = [];
            for (let i = 0; i < 7; ++i) {
                const dayStart = Moment.tz(startTime, timezone).add(i, 'days');
                const dayEnd = Moment.tz(dayStart, timezone).add(1, 'day');
                days[i] = { data: [], dayStart, dayEnd, color: dayStart < Moment() ? '#BBB' : '#EEEEEE' };
            }

            for (let i = 0; i < dataWithJournals.length; ++i) {
                let day = Moment(dataWithJournals[i].eventTime).startOf('day').diff(startTime, 'days');
                if (day >= 0 && day < 7)
                    days[day].data.push(dataWithJournals[i]);
            }

            for (let i = 0; i < 7; ++i) {
                const day = days[i];
                day.alert = findPeakAlert(day.data, overallAlertId, alertLevels);
                if (day.alert.peakLevel) {
                    day.color = day.alert.peakLevel.color;
                    day.alertName = day.alert.peakLevel.name;
                }
            }
            log.debug("days:", days);

            this.setState({
                progress: null, data, journals, days, startTime, endTime, timezone
            }, () => {
                log.debug(`loadContent done, ${Date.now() - loadContentTime} ms:`, this.state);
            });
        }).catch((err) => {
            log.error("loadContent error:", err);
            if (retries < 3) {
                this.loadContent(retries + 1);
            }
            else
                this.setState({ progress: null, error: getErrorMessage(err) });
        });
    }

    render() {
        const { days, progress, error, dialog, timezone } = this.state;
        //const { appContext: { state: { patient }}} = this.props;

        const gridItems = [];
        if ( days.length > 0 ) {
            const descDays = days.sort((a,b) => b.dayStart - a.dayStart);

            for(let i=0;i<descDays.length;++i) {
                const day = descDays[i];
                
                const complaintList = day.data
                    .reduce((p, c) => {
                        const f = c.source.find((j) => j.complaints);
                        if (f) {
                            p.push(...f.complaints);
                        }
                        return p;
                    }, [])
                    .reduce((p, c) => {
                        if (c.symptoms) {
                            p.push(...c.symptoms);
                        }
                        return p;
                    }, [])
                    .filter((v, i, a) => a.findIndex((j) => j.text === v.text) === i)          // de-duplicate the complaints
                    .map((complaint, i) => {
                        return <li key={i}>{complaint.text}</li>;
                    });
                //console.log("complaintList:", complaintList);
                const disposition = day.data
                    .filter((v) => v.dataId === 'disposition')
                    .map((disposition, i) => {
                        switch( Number(disposition.data[0]) ) {
                            case 1:     // referred to urgent care/emegency room
                            case 2:     // managed symptoms at home
                                return <li key={i}><PhoneCallbackIcon />{'    '}<T.span text='followUpComplete' /></li>;
                            case 3:     // unable to contact
                            case 4:     // patient declined
                                return <li key={i}><PhoneMissedIcon />{'    '}<T.span text='followUpAttempted' /></li>;
                            default:
                                log.error('Unknown disposition value:', disposition );
                                return null;
                        }
                    });
                //console.log("disposition:", disposition );

                gridItems.push(<Grid item style={{ minWidth: '100%' }} key={gridItems.length}>
                    {<React.Fragment>
                        <div style={{ backgroundColor: day.color, padding: "10px 5px", textAlign: "center", borderBottomLeftRadius: "35px" }}>
                            {<Typography variant="h4"> {Moment(day.dayEnd).tz(timezone).format("dddd")}</Typography>}
                            {<Typography variant='subtitle2'>{Moment(day.dayEnd).tz(timezone).format("MMMM DD, YYYY")}</Typography>}
                        </div>
                    </React.Fragment>}
                    {complaintList.length > 0 ? <ul>{complaintList}</ul> : <div align='center' style={{ padding: 15 }}>No activity reported</div>}
                    {disposition.length > 0 ? <ul style={{ listStyle: 'none', marginTop: 20}}>{disposition}</ul> : null}
                </Grid>)

            }
        }

        const icon = React.cloneElement( this.props.icon, {fontSize: 'small', style: {fill: this.props.color} } );
        return ( 
            <div id={this.props.id} >
                <Paper style={{textAlign: 'left', marginLeft: 15, marginRight: 15, marginTop: 5, marginBottom: 15, padding: 15}} >
                    <Grid container wrap="nowrap" style={{  marginBottom: 15, paddingBottom: 15, borderBottom: `1px solid grey` }} direction="row" alignItems="center">
                        <Grid item style={{ marginRight: 10 }}>
                            <Avatar style={{ width: 35, height: 35, border: `2px solid ${this.props.color}`, backgroundColor: 'white' }}>{icon}</Avatar>
                        </Grid>
                        <Grid item >
                            <span style={{ fontSize: 22, fontColor: '#666666' }}>{this.props.label}</span>
                        </Grid>
                    </Grid>
                    <Grid container  direction="row" justifyContent="center" style={styles.appointmentsHeader}>
                        <Grid item >
                            {progress || <span style={{color: 'red'}}>{error}</span>}
                        </Grid>
                    </Grid>
                    <Grid container direction="row" justifyContent="center" alignItems="center">
                        {gridItems}
                    </Grid>
                </Paper>
                {dialog}
            </div>
        )
        // const { progress, providers, dialog } = this.state;
        // let { appointments } = this.state;
        // const { appContext } = this.props;
        // const { patient } = appContext.state;
        
        // const confirmed = appointments.filter( (appointment) => appointment.status !== 'rejected')
 
        // let header = progress ? progress : confirmed ? <T.span text={{key: 'appointmentsWaiting', numAppointments: confirmed.length }} /> : 
        //     <T.span text='noAppointments' />;

        // let gridItems = [];
        // if ( confirmed ) {
        //     for(let i=0;i<confirmed.length;++i) {
        //         let appointment = confirmed[i];
        //         let providerRecord = providers.find((e) => e.provider.providerId === appointment.providerId);
        //         if (! providerRecord || !providerRecord.provider ) {
        //             console.error(`Provider ${appointment.providerId} not found!`);
        //             continue;      // invalid providerId
        //         }
        //         let provider = providerRecord.provider;
        //         let providerIcon = <ProviderIcon fontSize='large' />;   // TODO: use the provider image if found in the provider record
        //         let providerName = `${provider.firstName} ${provider.lastName}`;
        //         if ( provider.title )   
        //             providerName = `${provider.title} ` + providerName;

        //         let now = Moment();                                             // 1585176285664
        //         let days = Moment(appointment.start).diff(now,'days');
        //         let hours = Moment(appointment.start).diff(now,'hours');
        //         let minutes = Moment(appointment.start).diff(now, 'minutes');
        //         if ( hours > 0 )
        //             minutes = minutes % 60;
        //         log.debug(`days: ${days}, hours: ${hours}, minutes: ${minutes}`);

        //         let apptUpcoming = now > Moment(appointment.start).subtract(30,'minutes');
        //         let apptNow = now >= Moment(appointment.start);
        //         let apptDone = now > appointment.end;
        //         let canVideoChat = appointment.category === 'teleMedicine' && appointment.status === 'confirmed' && apptUpcoming && !apptDone;
        //         log.debug(`canVideoChat: ${canVideoChat}, apptNow: ${apptNow}, apptUpcoming: ${apptUpcoming}, apptDone: ${apptDone}`);
                
        //         let appointmentIn = days > 0 ? <T.span text={{key: 'days', days}} /> : 
        //             hours > 0 ? <T.span text={{key: 'hours', hours, minutes }} /> :
        //             <T.span text={{key: 'minutes', minutes}} />;
        //         let appointmentTime = Moment(appointment.start).local().format('MMMM Do YYYY, h:mm a');
                
        //         let actions = [];
        //         if ( appointment.status === 'requested') {
        //             actions.push(<Button disabled={progress !== null} key='reject' color="primary" variant="contained" style={styles.button} 
        //                 onClick={()=>{this.confirmRejectAppointment(appointment)}}><T.span text='reject' /></Button> );
        //             actions.push(<Button disabled={progress !== null} key='confirm' color="primary" variant="contained" style={styles.button} 
        //                 onClick={this.confirmAppointment.bind(this,appointment)}><T.span text='confirm' /></Button> );
        //         }
        //         if ( appointment.status === 'confirmed') {
        //             actions.push( <div key='confirmed' style={{color:'green', display:'flex', alignItems:'center'}}><CheckCircleIcon/><div style={{margin:'0px 5px'}}><T.span text='confirmed'/></div></div>)
        //         }

        //         let appointmentKey = apptNow && !apptDone ? 'appointmentNow' : 'appointment';

        //         gridItems.push(<div key={i} style={styles.appointment}>
        //             <Grid container wrap="nowrap" direction="row" justifyContent="flex-start" alignItems="center">
        //                 <Grid item style={{marginRight:'10px'}}>
        //                     {providerIcon}
        //                 </Grid>
        //                 <Grid item style={{marginBottom:'2px'}}>
        //                 <T.span text={{key: appointmentKey, providerName, appointmentTime, appointmentIn}} />
        //                 </Grid>
        //             </Grid>
        //             <Grid container style={{marginLeft:'40px',marginTop:'10px'}} direction="row" justifyContent="flex-start" alignItems="center">
        //                 {actions}
        //             </Grid>
        //             { canVideoChat && <div style={{marginLeft:'40px', marginTop:'10px'}}><Button key='videoChat' color="primary" variant="contained" style={styles.button} component={Link} to='/video'>
        //                 <T.span text='videoConf' /></Button></div>}
        //         </div>);
        //     }
        // }
        // const showRequestNewAppointmentButton = () => {
        //     //if patient, and patient has a provider to send a request to, show the button
        //     if (_.get(appContext, "state.provider.providerId")){
        //         return (
        //             <div style={styles.newAppointmentHeader}>
        //                 <Button disabled={!patient || patient.inactive === true} variant="outlined" onClick={ () => { this.openRequestDialog()}} >
        //                     <CreateIcon style={{marginRight:'5px'}}/>
        //                     <T.span text="requestAppointment" />
        //                 </Button>
        //             </div>
        //         )
        //     }
        // }

        // let icon = React.cloneElement( this.props.icon, {fontSize: 'small', style: {fill: this.props.color} } );
        // return ( 
        //     <div id={this.props.id} style={{marginTop:70}}>
        //         <Paper style={{textAlign: 'left', marginLeft: 15, marginRight: 15, marginTop: 5, marginBottom: 15, padding: 15}} >
        //             <Grid container wrap="nowrap" style={{  marginBottom: 15, paddingBottom: 15, borderBottom: `1px solid grey` }} direction="row" alignItems="center">
        //                 <Grid item style={{ marginRight: 10 }}>
        //                     <Avatar style={{ width: 35, height: 35, border: `2px solid ${this.props.color}`, backgroundColor: 'white' }}>{icon}</Avatar>
        //                 </Grid>
        //                 <Grid item >
        //                     <span style={{ fontSize: 22, fontColor: '#666666' }}>{this.props.label}</span>
        //                 </Grid>
        //             </Grid>
        //             <Grid container  direction="row" justifyContent="center" style={styles.appointmentsHeader}>
        //                 <Grid item >
        //                     {header}
        //                 </Grid>
        //             </Grid>
        //             {gridItems}
        //             {showRequestNewAppointmentButton()}
        //         </Paper>
        //         {dialog}
        //     </div>
        // )
    }
}

const styles = {
    gridContainer: {
        margin: 5,
        width: '95%'
    },
    newAppointmentHeader: {
        display:'flex',
        justifyContent:'center',
        margin:20
    },
    appointmentsHeader: {
        marginTop: 5,
        marginBottom: 15,
    },
    appointment: {
        //margin: '15px 30px 15px 30px',
        paddingBottom: '15px',
        //borderBottom: '1px solid grey'
    },
    button: {
        margin: 5,
    },
    headerPaper: {
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        padding: 10,
        margin: 5,
        width: '95%',
        textAlign: 'center'
    },
    paper: {
        padding: 10,
        margin: 5,
        width: '95%',
        textAlign: 'center'
    }
};